import { createClient } from "contentful";
import {
  setContentfulCareers,
  setContentfulEvents,
  setContentfulJob,
  setContentfulMeetCards,
  setContentfulGymnasticMeets,
  setContentfulPrograms,
  setContentfulStaff,
  setContentfulCalendar,
  setContentfulSiteAnnouncement,
} from "store/actions/contentfulActions";
import { normalizeGymnasticMeetEntry } from "helpers/contentful/normalizeGymnasticMeet";
import { resolveContentfulAssetUrl } from "helpers/contentful/resolveContentfulAssetUrl";
import { buildCalendarConfig, createEmptyCalendarConfig } from "helpers/contentful/normalizeCalendar";
import { normalizeSiteAnnouncement } from "helpers/contentful/normalizeSiteAnnouncement";
import { store } from "store/store";

/**
 * Custom hook for interacting with Contentful CMS
 *
 * This hook provides functions to fetch various content types from Contentful
 * and dispatch them to the Redux store. It handles programs, staff members,
 * events, careers, jobs, and meet cards.
 *
 * @returns {Object} An object containing functions to fetch different content types
 * @returns {Function} returns.getContentfulPrograms - Fetches program data from Contentful
 * @returns {Function} returns.getContentfulStaff - Fetches staff member data from Contentful
 * @returns {Function} returns.getContentfulEvents - Fetches events data from Contentful
 * @returns {Function} returns.getContentfulCareers - Fetches careers page data from Contentful
 * @returns {Function} returns.getContentfulJob - Fetches job listings from Contentful
 * @returns {Function} returns.getContentfulMeetCards - Fetches meet card data from Contentful
 *
 * @example
 * ```javascript
 * const { getContentfulPrograms, getContentfulStaff } = useContentful();
 *
 * // Fetch programs
 * await getContentfulPrograms();
 *
 * // Fetch staff members
 * await getContentfulStaff();
 * ```
 */
const useContentful = () => {
  /**
   * Contentful client instance
   * Configured with the gym's content space and access token
   */
  const client = createClient({
    space: "8xwfyz9o0vu9",
    accessToken: "M9I5ny01QN3dHYvWOSsh21VC2xSeGR8k6alKpn0CWmA",
    host: "cdn.contentful.com",
  });

  /**
   * Fetches program data from Contentful and dispatches to Redux store
   *
   * Retrieves all entries of content type "programTitles" and orders them
   * by the "name" field. The fetched data is automatically dispatched to
   * the Redux store using the setContentfulPrograms action.
   *
   * @async
   * @function getContentfulPrograms
   * @returns {Promise<void>} A promise that resolves when the data is fetched and stored
   * @throws {Error} Logs error to console if the fetch fails
   *
   * @example
   * ```javascript
   * const { getContentfulPrograms } = useContentful();
   * await getContentfulPrograms();
   * ```
   */
  const getContentfulPrograms = async () => {
    try {
      const entries = await client.getEntries({
        content_type: "programTitles",
        select: "fields",
        order: "fields.name",
      });
      store.dispatch(setContentfulPrograms(entries));
    } catch (error) {
      console.log(`Error Fetching Programs:${error}`);
    }
  };

  /**
   * Fetches staff member data from Contentful and dispatches to Redux store
   *
   * Retrieves all entries of content type "staffMembers" and orders them
   * by the "order" field. The fetched data is automatically dispatched to
   * the Redux store using the setContentfulStaff action.
   *
   * @async
   * @function getContentfulStaff
   * @returns {Promise<void>} A promise that resolves when the data is fetched and stored
   * @throws {Error} Logs error to console if the fetch fails
   *
   * @example
   * ```javascript
   * const { getContentfulStaff } = useContentful();
   * await getContentfulStaff();
   * ```
   */
  const getContentfulStaff = async () => {
    try {
      const entries = await client.getEntries({
        content_type: "staffMembers",
        select: "fields",
        order: "fields.order",
      });
      store.dispatch(setContentfulStaff(entries));
    } catch (error) {
      console.log(`Error Fetching Staff:${error}`);
    }
  };

  /**
   * Fetches events data from Contentful and dispatches to Redux store
   *
   * Retrieves all entries of content type "events" and orders them
   * by the "order" field. The fetched data is automatically dispatched to
   * the Redux store using the setContentfulEvents action.
   *
   * @async
   * @function getContentfulEvents
   * @returns {Promise<void>} A promise that resolves when the data is fetched and stored
   * @throws {Error} Logs error to console if the fetch fails
   *
   * @example
   * ```javascript
   * const { getContentfulEvents } = useContentful();
   * await getContentfulEvents();
   * ```
   */
  const getContentfulEvents = async () => {
    try {
      const entries = await client.getEntries({
        content_type: "events",
        select: "fields",
        order: "fields.order",
      });
      store.dispatch(setContentfulEvents(entries));
    } catch (error) {
      console.log(`Error Fetching Events:${error}`);
    }
  };

  /**
   * Fetches careers page data from Contentful and dispatches to Redux store
   *
   * Retrieves the careers page content with linked entries included up to 2 levels deep.
   * Processes the data to resolve linked entries and creates a structured object with
   * title and sections. The processed data is dispatched to the Redux store.
   *
   * @async
   * @function getContentfulCareers
   * @returns {Promise<void>} A promise that resolves when the data is fetched and stored
   * @throws {Error} Logs error to console if the fetch fails
   *
   * @example
   * ```javascript
   * const { getContentfulCareers } = useContentful();
   * await getContentfulCareers();
   * ```
   */
  const getContentfulCareers = async () => {
    try {
      const entries = await client.getEntries({
        content_type: "careerPage",
        select: "fields",
        include: 2, // This includes up to 2 levels of linked entries
      });
      // Process the entries to include resolved links
      const careerPage = entries.items[0].fields;
      const sections = careerPage.sections.map((section) => {
        const linkedEntry = entries.includes.Entry.find(
          (e) => e.sys.id === section.sys.id
        );
        return linkedEntry ? linkedEntry.fields : section;
      });
      const processedData = {
        title: careerPage.title,
        sections,
      };
      store.dispatch(setContentfulCareers(processedData));
    } catch (error) {
      console.log(`Error Fetching Careers: ${error}`);
    }
  };

  /**
   * Fetches job listings from Contentful and dispatches to Redux store
   *
   * Retrieves all entries of content type "job" and dispatches them to
   * the Redux store using the setContentfulJob action.
   *
   * @async
   * @function getContentfulJob
   * @returns {Promise<void>} A promise that resolves when the data is fetched and stored
   * @throws {Error} Logs error to console if the fetch fails
   *
   * @example
   * ```javascript
   * const { getContentfulJob } = useContentful();
   * await getContentfulJob();
   * ```
   */
  const getContentfulJob = async () => {
    try {
      const entries = await client.getEntries({
        content_type: "job",
        select: "fields",
      });
      store.dispatch(setContentfulJob(entries));
    } catch (error) {
      console.log(`Error Fetching Events:${error}`);
    }
  };

  /**
   * Fetches meet card data from Contentful and dispatches to Redux store
   *
   * Retrieves the meet card container content and extracts the cards array
   * from the first item's fields. The cards data is dispatched to the Redux store.
   *
   * @async
   * @function getContentfulMeetCards
   * @returns {Promise<void>} A promise that resolves when the data is fetched and stored
   * @throws {Error} Logs error to console if the fetch fails
   *
   * @example
   * ```javascript
   * const { getContentfulMeetCards } = useContentful();
   * await getContentfulMeetCards();
   * ```
   */
  const getContentfulMeetCards = async () => {
    try {
      const entries = await client.getEntries({
        content_type: "meetCardContainer",
        select: "fields",
      });
      store.dispatch(setContentfulMeetCards(entries.items[0].fields.cards));
    } catch (error) {
      console.log(`Error Fetching Events:${error}`);
    }
  };

  const getContentfulGymnasticMeets = async () => {
    try {
      const entries = await client.getEntries({
        content_type: "gymnasticMeets",
        include: 3,
        order: "fields.title",
      });
      const normalized = entries.items.map((item) =>
        normalizeGymnasticMeetEntry(item, entries.includes)
      );
      store.dispatch(setContentfulGymnasticMeets(normalized));
    } catch (error) {
      console.log(`Error Fetching Gymnastic Meets:${error}`);
    }
  };

  const getContentfulCalendar = async () => {
    try {
      const entries = await client.getEntries({
        content_type: "gymCalendar",
        select: "fields",
        limit: 1,
      });
      const fields = entries.items[0]?.fields;

      let termItems = [];
      try {
        const termEntries = await client.getEntries({
          content_type: "calendarTerm",
          select: "fields",
          order: "fields.order",
          limit: 50,
        });
        termItems = termEntries.items || [];
      } catch (termError) {
        console.log(`Calendar terms not loaded:${termError}`);
      }

      const config = buildCalendarConfig({
        calendarFields: fields,
        termItems,
      });

      store.dispatch(
        setContentfulCalendar({
          url: fields?.pdfFile
            ? resolveContentfulAssetUrl(fields.pdfFile)
            : undefined,
          label: fields?.navLabel?.trim() || "Calendar",
          config,
        })
      );
    } catch (error) {
      console.log(`Error Fetching Calendar:${error}`);
      store.dispatch(
        setContentfulCalendar({
          label: "Calendar",
          config: createEmptyCalendarConfig(),
        })
      );
    }
  };

  const getContentfulSiteAnnouncement = async () => {
    try {
      const entries = await client.getEntries({
        content_type: "siteAnnouncement",
        "fields.active": true,
        select: "fields",
        order: "-sys.updatedAt",
        limit: 1,
      });
      const fields = entries.items[0]?.fields;
      const announcement = normalizeSiteAnnouncement(fields);
      store.dispatch(setContentfulSiteAnnouncement(announcement));
    } catch (error) {
      console.log(`Error Fetching Site Announcement:${error}`);
      store.dispatch(setContentfulSiteAnnouncement(null));
    }
  };

  return {
    getContentfulPrograms,
    getContentfulStaff,
    getContentfulEvents,
    getContentfulCareers,
    getContentfulJob,
    getContentfulMeetCards,
    getContentfulGymnasticMeets,
    getContentfulCalendar,
    getContentfulSiteAnnouncement,
  };
};

export default useContentful;
